<template>
    <div v-if="show"
        class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center text-base">
        <div class="bg-white rounded-lg shadow-xl w-10/12 h-[96vh]">
            <div class="flex items-center justify-between p-4 border-b">
                <h2 class="text-2xl text-gray-800 font-bold">Register Patient</h2>
                <button type="button"
                    class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                    @click="$emit('close')" aria-label="Close">
                    <span class="sr-only">Dismiss</span>
                    <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 14 14">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                    </svg>
                </button>
            </div>


            <div v-if="showPatientPopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div class="bg-white p-6 shadow-xl w-6/12 rounded-2xl flex flex-col min-h-[300px] max-h-[80vh]">
                    <h3 class="text-lg font-bold mb-4">Select Patient</h3>

                    <div class="flex-grow overflow-y-auto">
                        <div class="space-y-2">
                            <template v-if="matchingPatients.length > 0">
                                <div v-for="patient in matchingPatients" :key="patient.id"
                                    class="grid grid-cols-12 items-center py-3 bg-gray-50">
                                    <div class="col-span-5">
                                        <div class="text-sm text-gray-600">Name:</div>
                                        <div class="font-medium">{{ patient.form.fullName }}</div>
                                    </div>

                                    <div class="col-span-5">
                                        <div class="text-sm text-gray-600">Relationship:</div>
                                        <div class="font-medium">{{ patient.form.relationship }}</div>
                                    </div>

                                    <div class="col-span-2 text-right">
                                        <button @click="selectPatient(patient)"
                                            class="px-4 py-2 bg-custom-blue text-white rounded hover:bg-blue-800 transition-colors text-sm">
                                            Select
                                        </button>
                                    </div>
                                </div>
                            </template>
                            <div v-else class="text-center py-4 text-gray-500">
                                No matching patients found.
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 flex justify-end">
                        <button @click="showPatientPopup = false"
                            class="px-6 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200 transition-colors">
                            Close
                        </button>
                    </div>
                </div>
            </div>


            <div class="p-4">
                <div class="flex space-x-4 mb-6 border-b">
                    <button v-for="(tab, index) in tabs" :key="index" @click="activeTab = index"
                        :class="['px-4 py-2 font-semibold', activeTab === index ? 'border-b-2 border-custom-yellow text-custom-blue' : 'text-gray-500']">
                        {{ tab }}
                    </button>
                </div>

                <div v-if="activeTab === 0">
                    <form @submit.prevent class="space-y-4 overflow-auto h-[70vh]">
                        <div class="grid grid-cols-2 gap-4 border-b pb-4 px-10">
                            <div>
                                <label class="block text-sm font-bold text-gray-700">ID Number/Passport Number <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.idNumber" type="text" placeholder="Type..."
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Phone Number <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.phoneNumber" type="text" placeholder="Type..."
                                    @input="debouncedLookupPatient"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Full Name <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.fullName" type="text" placeholder="Type..."
                                    @input="debouncedLookupPatient"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Title <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.title"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">Mr/Mrs/Miss/Madam</option>
                                    <option value="Mr">Mr</option>
                                    <option value="Mrs">Mrs</option>
                                    <option value="Miss">Miss</option>
                                    <option value="Madam">Madam</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">D.O.B <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.dob" type="date"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Age <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.age" type="text" placeholder="Auto-filled" readonly
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Gender <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.gender"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">Male, Female</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Relationship <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.relationship"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">Principal Owner/Dependent</option>
                                    <option value="Male">Principal Owner</option>
                                    <option value="Female">Dependent</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Visit Type <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.visitType"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">Consultation/Triage Procedure</option>
                                    <option value="Consultation">Consultation</option>
                                    <option value="Triage">Triage</option>
                                    <option value="Procedure">Procedure</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Blood Group <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.bloodGroup"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">A+,A-,AB+,AB-,O-,O+</option>
                                    <option value="A+">A+</option>
                                    <option value="A-">A-</option>
                                    <option value="AB+">AB+</option>
                                    <option value="AB-">AB-</option>
                                    <option value="O-">O-</option>
                                    <option value="O+">O+</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Email Address <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.emailAddress" type="email" placeholder="Type..."
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Occupation <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.occupation" type="text" placeholder="Type..."
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Marital Status <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.maritalStatus"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">eg Single,Married,Widowed,Divorced,Separated,Registered
                                        Partnerships</option>
                                    <option value="Single">Single</option>
                                    <option value="Married">Married</option>
                                    <option value="Widowed">Widowed</option>
                                    <option value="Divorced">Divorced</option>
                                    <option value="Separated">Separated</option>
                                    <option value="Registered Partnerships">Registered Partnerships</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Residence <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.residence" type="text" placeholder="Type...eg Kenyan"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">County <span
                                        class="text-red-500">*</span></label>
                                <select v-model="form.county"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                                    <option value="">Select (Drop down of all 47 counties)</option>
                                    <!-- Add options for all 47 counties here -->
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-bold text-gray-700">Nationality <span
                                        class="text-red-500">*</span></label>
                                <input v-model="form.nationality" type="text" placeholder="Type...eg Kenyan"
                                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
                            </div>
                        </div>
                    </form>
                    <div class="flex justify-end space-x-2 py-4">
                        <button type="button" @click="$emit('close')"
                            class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50">
                            Cancel
                        </button>
                        <button type="button" @click="nextTab"
                            class="px-4 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
                            Next
                        </button>
                    </div>
                </div>


                <!-- Emergency Contact Tab -->
                <div v-if="activeTab === 1" class="relative w-full px-8 py-6">
                    <!-- Container: Form and Add Button -->
                    <div class="flex items-start justify-between border-b pb-4">
                        <!-- Left Side: Emergency Contact Form -->
                        <div class="w-1/2 pr-4">
                            <form @submit.prevent class="space-y-6">
                                <div class="pb-4 space-y-4">
                                    <div>
                                        <label class="block text-sm font-bold text-gray-700">Full Name <span
                                                class="text-red-500">*</span></label>
                                        <input v-model="emergencyContact.fullName" type="text" placeholder="Type..."
                                            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 h-12">
                                    </div>
                                    <div>
                                        <label class="block text-sm font-bold text-gray-700">Relationship <span
                                                class="text-red-500">*</span></label>
                                        <input v-model="emergencyContact.relationship" type="text" placeholder="Type..."
                                            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 h-12">
                                    </div>
                                    <div>
                                        <label class="block text-sm font-bold text-gray-700">Phone Number <span
                                                class="text-red-500">*</span></label>
                                        <input v-model="emergencyContact.phoneNumber" type="text" placeholder="Type..."
                                            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 h-12">
                                    </div>
                                </div>
                            </form>
                        </div>

                        <!-- Right Side: Add Button -->
                        <div class="w-1/2 flex items-start justify-end pl-4">
                            <button @click="addEmergencyContact"
                                class="text-custom-yellow hover:text-custom-yellow-dark border-4 border-custom-yellow  p-1">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4"
                                        d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Display Added Emergency Contacts in Table Format -->
                    <div v-if="emergencyContacts.length" class="mt-6">
                        <h3 class="text-lg font-bold text-gray-800 mb-4">Added Emergency Contacts</h3>
                        <table class="w-full bg-white rounded-md shadow-md">
                            <thead>
                                <tr class="bg-gray-100 border-b">
                                    <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Full Name</th>
                                    <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Relationship</th>
                                    <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Phone Number</th>
                                    <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(contact, index) in emergencyContacts" :key="index" class="border-b">
                                    <td class="py-2 px-4">{{ contact.fullName }}</td>
                                    <td class="py-2 px-4">{{ contact.relationship }}</td>
                                    <td class="py-2 px-4">{{ contact.phoneNumber }}</td>
                                    <td class="py-2 px-4 space-x-2">
                                        <button @click="deleteEmergencyContact(index)"
                                            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Bottom Section: Cancel and Next Buttons -->
                    <div class="flex justify-end space-x-4 mt-8">
                        <button type="button" @click="previousTab"
                            class="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50">
                            Cancel
                        </button>
                        <button type="button" @click="nextTab"
                            class="px-6 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
                            Next
                        </button>
                    </div>
                </div>

                <!-- Payment Info -->
                <div v-if="activeTab === 2" class="space-y-6 flex flex-col justify-center items-center mt-6 ">
                    <div class="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                        <h2 class="text-gray-600 font-medium mb-4">Select Payment Method</h2>
                        <div class="flex justify-between mb-6">
                            <button v-for="method in paymentMethods" :key="method.id"
                                class="flex flex-col items-center justify-center p-4 rounded-lg relative" :class="[
                                    selectedMethod === method.id
                                        ? 'bg-blue-50 border border-blue-500'
                                        : 'bg-gray-50'
                                ]" @click="selectedMethod = method.id">
                                <img class="object-cover w-12 h-12 rounded-full" :src="method.url" alt="Your avatar" />
                                <span class="text-gray-400">{{ method.name }}</span>
                                <span v-if="selectedMethod === method.id"
                                    class="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                    <span class="text-white text-xs">✓</span>
                                </span>
                            </button>
                        </div>

                        <div v-if="selectedMethod === 'walkin'" class="mt-6">
                            <h3 class="text-gray-600 font-medium mb-2">Select Mobile Money</h3>
                            <div class="relative">
                                <select v-model="selectedMobileProvider"
                                    class="w-full p-2 pr-8 border border-gray-300 rounded-md appearance-none bg-white text-gray-400">
                                    <option>Safaricom</option>
                                    <option>Airtel</option>
                                    <option>Other</option>
                                </select>
                                <span class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                                    ▼
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Bottom Section: Cancel and Next Buttons -->
                    <div class="flex justify-end space-x-4 mt-8">
                        <button type="button" @click="previousTab"
                            class="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50">
                            Cancel
                        </button>
                        <button type="button" @click="nextTab"
                            class="px-6 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
                            Next
                        </button>
                    </div>
                </div>


                <!-- Documents Upload -->
                <div v-if="activeTab === 3" class="space-y-6">
                    <div class="border border-gray-300 rounded-lg p-6 w-1/2 mx-auto">
                        <h3 class="text-gray-600 font-medium mb-4">Capture/Upload referral letter or photo</h3>
                        <div class="flex flex-wrap gap-4">
                            <!-- File upload area -->
                            <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center w-48 h-48 cursor-pointer"
                                @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
                                <input type="file" ref="fileInput" class="hidden" @change="handleFileSelect"
                                    accept="image/*,.pdf">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400 mb-2" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p class="text-sm text-gray-500">Drag and drop your files here</p>
                                <p class="text-sm text-gray-500">OR</p>
                                <button
                                    class="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50">
                                    Browse files
                                </button>
                            </div>

                            <!-- Uploaded document previews -->
                            <div v-for="(doc, index) in uploadedDocuments" :key="index" class="relative">
                                <img :src="doc.preview" alt="Uploaded document"
                                    class="w-48 h-48 object-cover rounded-lg">
                                <button @click="removeDocument(index)"
                                    class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Bottom Section: Cancel and Next Buttons -->
                    <div class="flex justify-end space-x-4 mt-8">
                        <button type="button" @click="previousTab"
                            class="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50">
                            Cancel
                        </button>
                        <button type="button" @click="nextTab"
                            class="px-6 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
                            Next
                        </button>
                    </div>
                </div>

                <!-- Documents Upload -->
                <div v-if="activeTab === 4" class="space-y-6">
                    <!-- Checkmarks Section -->
                    <div class="border border-gray-300 rounded-lg p-6 w-1/2 mx-auto">
                        <h3 class="text-gray-600 font-medium mb-4">Agreement Acknowledgements</h3>
                        <div class="space-y-4">
                            <div class="flex items-center">
                                <input type="checkbox" v-model="agreements.privacyPolicy" id="privacyPolicy"
                                    class="form-checkbox h-5 w-5 text-custom-yellow focus:ring-custom-yellow">
                                <label for="privacyPolicy" class="ml-3 text-gray-600">Privacy Policy
                                    Acknowledgement</label>
                            </div>

                            <div class="flex items-center">
                                <input type="checkbox" v-model="agreements.financialResponsibility"
                                    id="financialResponsibility"
                                    class="form-checkbox h-5 w-5 text-custom-yellow focus:ring-custom-yellow">
                                <label for="financialResponsibility" class="ml-3 text-gray-600">Financial Responsibility
                                    Agreement</label>
                            </div>

                            <div class="flex items-center">
                                <input type="checkbox" v-model="agreements.consentForTreatment" id="consentForTreatment"
                                    class="form-checkbox h-5 w-5 text-custom-yellow focus:ring-custom-yellow">
                                <label for="consentForTreatment" class="ml-3 text-gray-600">Consent for
                                    Treatment</label>
                            </div>
                        </div>
                    </div>

                    <!-- Bottom Section: Cancel and Next Buttons -->
                    <div class="flex justify-end space-x-4 mt-8">
                        <button type="button" @click="previousTab"
                            class="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50">
                            Cancel
                        </button>
                        <button type="button" @click="submitForm"
                            class="px-6 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
                            Submit
                        </button>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";
import capitation from '@/assets/images/capitation.png';
import ffs from '@/assets/images/ffs.png';
import walkin from '@/assets/images/walkin.png';
import { debounce } from 'lodash'; // Make sure to install lodash: npm install lodash


export default {
    name: 'PatientRegistrationModal',
    props: {
        show: {
            type: Boolean,
            required: true
        }
    },
    created() {
        // Create a debounced version of the lookupPatient method
        this.debouncedLookupPatient = debounce(this.lookupPatient, 300);
    },
    data() {
        return {
            activeTab: 0,
            tabs: [
                'Personal Information',
                'Emergency Contact',
                'Payment Info',
                'Documents Upload',
                'Declaration'
            ],
            matchingPatients: [],
            showPatientPopup: false,
            debouncedLookupPatient: null,
            form: {
                title: '',
                fullName: '',
                dob: '',
                age: '',
                gender: '',
                relationship: '',
                idNumber: '',
                visitType: '',
                bloodGroup: '',
                phoneNumber: '',
                emailAddress: '',
                occupation: '',
                maritalStatus: '',
                residence: '',
                county: '',
                nationality: '',
            },
            emergencyContact: {
                fullName: '',
                relationship: '',
                phoneNumber: ''
            },
            emergencyContacts: [],
            uploadedDocuments: [],
            selectedMethod: 'walkin',
            selectedMobileProvider: 'Safaricom',
            paymentMethods: [
                { id: 'capitation', name: 'Capitation', url: capitation },
                { id: 'ffs', name: 'FFS', url: ffs },
                { id: 'walkin', name: 'Walk-In', url: walkin },
            ],
            agreements: {
                privacyPolicy: false,
                financialResponsibility: false,
                consentForTreatment: false,
            },
        }
    },
    watch: {
        'form.dob': function (newDob) {
            if (newDob) {
                const birthDate = new Date(newDob);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                this.form.age = age.toString();
            } else {
                this.form.age = '';
            }
        }
    },
    methods: {
        async lookupPatient() {
            // Only proceed if both fullName and phoneNumber have values
            if (!this.form.idNumber || !this.form.phoneNumber) {
                this.showPatientPopup = false;
                return;
            }

            try {
                // Fetch all patient data (replace with your actual API endpoint)
                const response = await fetch('/patient-details.json');
                const allPatientData = await response.json();

                // Filter patients based on fullName and phoneNumber
                this.matchingPatients = allPatientData.filter(patient =>
                    patient.form.idNumber.includes(this.form.idNumber) &&
                    patient.form.phoneNumber.includes(this.form.phoneNumber)
                );

                this.showPatientPopup = this.matchingPatients.length > 0;
            } catch (error) {
                console.error('Error looking up patient:', error);
            }
        },

        selectPatient(patient) {
            // Fill in the form with the selected patient's data
            Object.keys(this.form).forEach(key => {
                if (patient.form[key]) {
                    this.form[key] = patient.form[key];
                }
            });

            // Fill in emergency contacts if available
            if (patient.emergencyContacts) {
                this.emergencyContacts = [...patient.emergencyContacts];
            }

            this.showPatientPopup = false;
        },

        async submitForm() {
            const formData = {
                ...this.form,
                emergencyContact: this.emergencyContact,
                emergencyContacts: this.emergencyContacts,
                uploadedDocuments: this.uploadedDocuments,
                selectedMethod: this.selectedMethod,
                selectedMobileProvider: this.selectedMobileProvider,
                agreements: this.agreements,
            };

            // Check if all agreements are accepted
            const allAgreementsAccepted = Object.values(this.agreements).every(value => value);

            if (allAgreementsAccepted) {
                try {
                    console.log("Submitting form data:", formData);

                    // API call
                    const response = await api.createPatient(formData)
                    console.log("API Response:", response);

                    // Success message or navigation
                    alert("Form submitted successfully!");
                    // Optional: Navigate to another page or reset form
                    // this.$router.push('/success-page');
                    // this.resetForm();
                } catch (error) {
                    // Handle API error
                    console.error("Error submitting form:", error);
                    alert(`Failed to submit form: ${error.message}`);
                }
            } else {
                alert("Please fill in all required fields and check all consent boxes.");
            }
        },


        nextTab() {
            if (this.activeTab < this.tabs.length - 1) {
                this.activeTab++;
            } else {
                // Handle form completion
                console.log('Form completed');
            }
        },
        previousTab() {
            if (this.activeTab > 0) {
                this.activeTab--;
            }
        },
        addEmergencyContact() {
            // Check if all fields are filled before adding to the list
            if (this.emergencyContact.fullName && this.emergencyContact.relationship && this.emergencyContact.phoneNumber) {
                // Add the new contact
                this.emergencyContacts.push({ ...this.emergencyContact });
                // Reset the form fields for the next entry
                this.emergencyContact = { fullName: '', relationship: '', phoneNumber: '' };
            } else {
                alert("Please fill in all fields before adding the contact.");
            }
        },
        deleteEmergencyContact(index) {
            // Remove the selected contact from the list
            this.emergencyContacts.splice(index, 1);
        },


        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileSelect(event) {
            this.addDocuments(event.target.files);
        },
        handleFileDrop(event) {
            this.addDocuments(event.dataTransfer.files);
        },
        addDocuments(files) {
            for (let file of files) {
                if (file.type.startsWith('image/') || file.type === 'application/pdf') {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.uploadedDocuments.push({
                            file: file,
                            preview: e.target.result
                        });
                    };
                    reader.readAsDataURL(file);
                } else {
                    alert('Please upload only image or PDF files.');
                }
            }
        },
        removeDocument(index) {
            this.uploadedDocuments.splice(index, 1);
        }


    }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>